import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";

import { auth } from "config/firebaseConfig";
import { useToastStore } from "store/toastStore";
import { VerifyAccountApi } from "apis/authServices";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err: any) {
    throw new Error(err?.message || "Failed to send reset email.");
  }
};

const ForgetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    try {
      // Check whether the email belongs to an existing account first.
      const verifyResponse = await VerifyAccountApi(email.trim());
      const statusId = verifyResponse?.statusId ?? null;
      if (statusId === null) {
        setError("This email does not belong to an account. Please use an existing email or sign up.");
        return;
      }

      await resetPassword(email);
      useToastStore.getState().showToast("Password reset instructions sent to your email.", "success");
    } catch (err: any) {
      setError(err?.message || "Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="lg:col-span-3 col-span-12 lg:col-start-9">
      <div>
        <img className="w-40" src="/images/logo.svg" alt="logo" />
        <p className="text-sm mt-8 font-normal text-grease-50">Forgot your password?</p>
        <p className="text-2xl mt-3 font-semibold text-grease-100">Reset your password</p>

        <form onSubmit={handleSubmit} className="mt-8">
          <input
            type="text"
            name="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full p-4 rounded-xl bg-gray-50 text-sm text-grease-100 placeholder-gray-400 focus:outline-none border-0 focus:border-light-yellow border-transparent focus:border focus:ring-1 focus:ring-light-yellow"
            placeholder="Email Address"
          />
          {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full p-4 rounded-lg text-white text-center shadow-button bg-light-yellow text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Sending..." : "Send reset link"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate("/login")}
          className="text-light-yellow text-sm font-medium mt-6 inline-flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default ForgetPassword;
